import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaCaretDown } from "react-icons/fa";
import { SlArrowRight } from "react-icons/sl";
import { useUser } from "../../../../context/user-context";
import { formatAmount } from "../../../../utils/formatTime";
import GetRates from "../../../../components/utils/GetRates";
import useAuthAxios from "../../../../utils/baseAxios";
import { API } from "../../../../constants/api";
import { utils, NAIRA_WALLET_ENABLED } from "../../../../constants/constants";
import {
  DataPaymentSource,
  DataProduct,
  DataProvider,
} from "./types";
import PaymentSourceSelect from "../PaymentSourceSelect";

type BuyDataProps = {
  setOpenData: (open: boolean) => void;
  setOpenDataPreview: (open: boolean) => void;
  amount: string;
  setAmount: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  network: string;
  setNetwork: (value: string) => void;
  networkName: string;
  setNetworkName: (value: string) => void;
  planName: string;
  setPlanName: (value: string) => void;
  productSlug: string;
  setProductSlug: (value: string) => void;
  paymentSource: DataPaymentSource;
  setPaymentSource: (value: DataPaymentSource) => void;
};

// Prefer our bundled logos: the backend's `logoUrl` points at
// assets.payscribe.ng, which doesn't resolve, so those images break.
const getProviderLogo = (slug: string, logoUrl: string | null) => {
  const match = utils.find(
    (item) =>
      item.abbr?.toLowerCase() === slug.toLowerCase() ||
      item.name?.toLowerCase() === slug.toLowerCase()
  );
  return match?.logo ?? logoUrl ?? "";
};

const BuyData: React.FC<BuyDataProps> = ({
  setOpenData,
  setOpenDataPreview,
  amount,
  setAmount,
  phone,
  setPhone,
  network,
  setNetwork,
  networkName,
  setNetworkName,
  planName,
  setPlanName,
  productSlug,
  setProductSlug,
  paymentSource,
  setPaymentSource,
}) => {
  const axiosInstance = useAuthAxios();
  const { userDetails } = useUser();
  const walletBalances = userDetails?.user.deexWalletBalances;
  const [openNet, setOpenNet] = useState(false);
  const [openPlans, setOpenPlans] = useState(false);

  const usdtBalance = walletBalances?.usdt ?? 0;
  const ngnBalance = walletBalances?.ngn ?? 0;
  const usdtRate = GetRates("USDT");
  const hasAmount = amount !== "" && amount != null;

  const { data: plansResponse, isLoading: loadingPlans, isError } = useQuery({
    queryKey: ["data-plans"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getDataPlans);
      return response.data;
    },
    retry: 1,
  });

  const providers: DataProvider[] = useMemo(
    () => plansResponse?.data ?? [],
    [plansResponse?.data]
  );

  const selectedProvider = useMemo(
    () => providers.find((provider) => provider.slug === network),
    [providers, network]
  );

  const productsForNetwork: DataProduct[] = selectedProvider?.products ?? [];

  const isBalanceSufficient = hasAmount
    ? paymentSource === "NGN"
      ? Number(amount) <= Number(ngnBalance)
      : Number(amount) / Number(usdtRate) <= Number(usdtBalance)
    : false;

  const validate = () => {
    if (!network) {
      toast.error("Kindly select a network");
      return false;
    }
    if (!planName || !productSlug) {
      toast.error("Kindly select a data plan");
      return false;
    }
    if (!phone || phone.length < 11) {
      toast.error("Kindly enter a valid phone number");
      return false;
    }
    if (!hasAmount) {
      toast.error("Plan price unavailable. Please select another plan.");
      return false;
    }
    if (!isBalanceSufficient) {
      toast.error("Insufficient funds");
      return false;
    }
    return true;
  };

  const handleProceed = () => {
    if (!validate()) return;
    setOpenData(false);
    setOpenDataPreview(true);
  };

  const handleNetworkSelect = (provider: DataProvider) => {
    setNetwork(provider.slug);
    setNetworkName(provider.name);
    setPlanName("");
    setProductSlug("");
    setAmount("");
    setOpenNet(false);
    setOpenPlans(false);
  };

  const handlePlanSelect = (product: DataProduct) => {
    setPlanName(product.name);
    setProductSlug(product.slug);
    setAmount(product.amount != null ? String(product.amount) : "");
    setOpenPlans(false);
  };

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load data plans");
    }
  }, [isError]);

  useEffect(() => {
    if (plansResponse === undefined) return;
    if (!providers.length) {
      toast.error("No data plans available");
    }
  }, [providers.length, plansResponse]);

  const networkLogo = network
    ? getProviderLogo(network, selectedProvider?.logoUrl ?? null)
    : "";

  return (
    <div className="fixed inset-0 flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:w-2/5 xxl:w-1/3">
        <div className="flex w-full items-center justify-start gap-6">
          <button type="button" onClick={() => setOpenData(false)}>
            <BsArrowLeft className="text-[20px] text-black dark:text-white" />
          </button>
          <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
            Buy Data
          </h4>
        </div>

        <div className="mt-10 w-full">
          <PaymentSourceSelect
            value={paymentSource}
            onChange={setPaymentSource}
          />

          <div className="relative mt-6 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">Network</h4>
            <button
              type="button"
              onClick={() => {
                setOpenNet((prev) => !prev);
                setOpenPlans(false);
              }}
              className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 px-4 py-3 text-[14px] capitalize shadow dark:border-gray-700"
            >
              {network ? (
                <div className="flex items-center gap-2">
                  {networkLogo && (
                    <img
                      src={networkLogo}
                      alt={networkName}
                      className="h-[24px] w-[24px] rounded-full"
                    />
                  )}
                  <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                    {networkName}
                  </span>
                </div>
              ) : (
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                  {loadingPlans ? "Loading networks..." : "Select network"}
                </span>
              )}
              <FaCaretDown className="pointer-events-none text-[20px] text-gray-900 dark:text-gray-200" />
            </button>

            {openNet && (
              <div className="absolute z-50 mt-2 max-h-[280px] w-full overflow-auto rounded-xl bg-white p-2 shadow-lg dark:bg-gray-800">
                {loadingPlans ? (
                  <p className="p-4 text-center text-[13px] text-gray-500">
                    Loading...
                  </p>
                ) : (
                  providers.map((provider) => {
                    const logo = getProviderLogo(
                      provider.slug,
                      provider.logoUrl
                    );
                    return (
                      <button
                        key={provider.id}
                        type="button"
                        onClick={() => handleNetworkSelect(provider)}
                        className="mt-2 flex w-full cursor-pointer items-center justify-between rounded-xl border border-gray-200 p-4 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          {logo && (
                            <img
                              src={logo}
                              alt={provider.name}
                              className="h-[24px] w-[24px] rounded-full"
                            />
                          )}
                          <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                            {provider.name}
                          </span>
                        </div>
                        <SlArrowRight className="text-[15px] text-gray-600 dark:text-gray-400" />
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="relative mt-6 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">Plan</h4>
            <button
              type="button"
              onClick={() => {
                if (network) {
                  setOpenPlans((prev) => !prev);
                  setOpenNet(false);
                }
              }}
              disabled={!network}
              className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 px-4 py-3 text-[14px] shadow disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700"
            >
              {planName ? (
                <span className="text-left text-[13px] font-medium text-gray-500 dark:text-gray-400">
                  {planName}
                </span>
              ) : (
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                  {network ? "Select plan" : "Select a network first"}
                </span>
              )}
              <FaCaretDown className="pointer-events-none text-[20px] text-gray-900 dark:text-gray-200" />
            </button>

            {openPlans && productsForNetwork.length > 0 && (
              <div className="absolute z-50 mt-2 max-h-[320px] w-full overflow-auto rounded-xl bg-white p-2 shadow-lg dark:bg-gray-800">
                {productsForNetwork.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handlePlanSelect(product)}
                    className="mt-2 flex w-full cursor-pointer flex-col rounded-xl border border-gray-200 p-4 text-left dark:border-gray-600"
                  >
                    <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                      {product.name}
                    </span>
                    <span className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">
                      {product.amount != null
                        ? `₦${formatAmount(product.amount)} NGN`
                        : "--"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8">
            <h4 className="text-[14px] font-bold text-gray-500">
              Phone Number
            </h4>
            <input
              type="tel"
              placeholder="Enter phone number"
              value={phone}
              maxLength={11}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value.length > 11) value = value.slice(0, 11);
                if (value === "" || value[0] === "0") {
                  setPhone(value);
                }
              }}
              className="mt-3 h-[45px] w-full rounded-[4px] border border-[#EAEAEA] bg-transparent p-3 text-left text-[13px] text-gray-700 shadow outline-none dark:border-gray-700 dark:text-gray-300"
            />
          </div>

          <div className="mt-8 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">Amount</h4>
            <div className="mt-3 flex w-full items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 shadow dark:border-gray-700">
              <h4 className="text-[13px] font-bold text-black dark:text-white">
                NGN
              </h4>
              <div className="w-[40%]">
                <div className="flex w-full items-center justify-end text-gray-900 dark:text-gray-200">
                  {hasAmount ? (
                    <>
                      <span className="font-sora text-[18px] font-bold">₦</span>
                      <span className="text-right text-[18px] font-semibold">
                        {formatAmount(amount)}
                      </span>
                    </>
                  ) : (
                    <span className="text-right text-[18px] font-semibold">
                      --
                    </span>
                  )}
                </div>
                {paymentSource === "USDT" && (
                  <h4 className="mt-1 pr-2 text-right text-[12px] font-medium text-gray-500">
                    {hasAmount
                      ? `${formatAmount(Number(amount) / Number(usdtRate))} USDT`
                      : "--"}
                  </h4>
                )}
              </div>
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between">
            {NAIRA_WALLET_ENABLED && (
              <h4 className="text-[12px] font-bold text-[#BE6B0A]">
                Balance:{" "}
                {paymentSource === "NGN"
                  ? `₦${formatAmount(ngnBalance)}`
                  : `${formatAmount(usdtBalance)} USDT`}
              </h4>
            )}
            {hasAmount && !isBalanceSufficient && (
              <h4 className="text-[12px] font-bold italic text-red-600">
                Insufficient funds
              </h4>
            )}
          </div>

          <button
            type="button"
            disabled={
              !network ||
              !planName ||
              !phone ||
              !hasAmount ||
              !isBalanceSufficient ||
              loadingPlans
            }
            onClick={handleProceed}
            className={
              !network ||
              !planName ||
              !phone ||
              !hasAmount ||
              !isBalanceSufficient ||
              loadingPlans
                ? "mt-12 flex h-[48px] w-full items-center justify-center rounded-xl bg-gray-400 text-[15px] font-medium text-gray-200"
                : "mt-12 flex h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-medium text-white"
            }
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyData;
